import { GridColumnHeader, SortDirection } from '../components/Grid/types';
import { Attribute, DataSource } from '../types/entities.types';

const gridHeaders: GridColumnHeader[] = [
  {
    name: 'businessMetadata.asset_name',
    label: 'Endpoint Name',
    width: '30%',
    filter: {
      type: 'text',
      remoteSearchFields: ['asset_name'],
    },
    sortable: true,
    remoteSortField: 'asset_name',
    defaultSortBy: 'asset_name',
    defaultSortType: SortDirection.ASC,
  },
  {
    name: 'technicalMetadata.asset_status',
    label: 'Status',
    width: '20%',
    filter: {
      type: 'aggregation',
      aggregationName: 'asset_status_aggregation',
      remoteSearchFields: ['asset_status'],
      filterSelectProps: {
        isClearable: true,
      },
    },
  },
  {
    name: 'technicalMetadata.created_by',
    label: 'Created By',
    width: '20%',
    filter: {
      type: 'text',
      remoteSearchFields: ['created_by'],
    },
  },
  {
    name: 'sourceSpecificMetadata.MLEndpoint.route_optimized',
    label: 'Route Optimized',
    width: '15%',
  },
  {
    name: 'businessMetadata.descriptiveMetadata.description',
    label: 'Description',
    width: '15%',
  },
];

const attributesMap: Attribute[] = [
  // Column 0 - Basic Information
  {
    name: 'businessMetadata.asset_id',
    label: 'Endpoint ID',
    showInColumn: 0,
    description: 'Unique identifier for the ML endpoint',
  },
  {
    name: 'technicalMetadata.created_time',
    label: 'Created',
    showInColumn: 0,
    type: 'date',
    description: 'When the endpoint was created',
  },
  {
    name: 'technicalMetadata.usageMetadata.last_altered',
    label: 'Last Modified',
    showInColumn: 0,
    type: 'date',
    description: 'When the endpoint was last modified',
  },
  {
    name: 'technicalMetadata.created_by',
    label: 'Created By',
    showInColumn: 0,
    description: 'User who created the endpoint',
  },
  {
    name: 'technicalMetadata.asset_status',
    label: 'Status',
    showInColumn: 0,
    description: 'Current status of the endpoint',
  },

  // Column 1 - Configuration
  {
    name: 'sourceSpecificMetadata.MLEndpoint.creator_kind',
    label: 'Creator Type',
    showInColumn: 1,
    description: 'Type of creator (User, System, etc.)',
  },
  {
    name: 'sourceSpecificMetadata.MLEndpoint.permissions',
    label: 'Permissions',
    showInColumn: 1,
    description: 'Access permissions for the endpoint',
  },
  {
    name: 'sourceSpecificMetadata.MLEndpoint.route_optimized',
    label: 'Route Optimized',
    type: 'boolean',
    showInColumn: 1,
    description: 'Whether the endpoint route is optimized',
  },
  {
    name: 'sourceSpecificMetadata.MLEndpoint.task',
    label: 'Task Type',
    showInColumn: 1,
    description: 'Type of ML task (e.g., llm/v1/chat)',
  },

  // Column 2 - Advanced Configuration
  {
    name: 'sourceSpecificMetadata.MLEndpoint.tags',
    label: 'Tags',
    showInColumn: 2,
    description: 'Tags associated with the endpoint',
    type: 'table',
    tableColumns: ['Name', 'Value'],
    tableFieldMapping: {
      Name: 'name',
      Value: 'value',
    },
  },
  {
    name: 'sourceSpecificMetadata.MLEndpoint.oauth_info',
    label: 'OAuth Configuration',
    type: 'link',
    showInColumn: 1,
    description: 'OAuth configuration URL',
  },
  {
    name: 'sourceSpecificMetadata.MLEndpoint.custom_entities',
    label: 'Custom Entities',
    type: 'code',
    showInColumn: 1,
    description: 'Custom entity configurations',
  },
  {
    name: 'sourceSpecificMetadata.MLEndpoint.foundational_entities',
    label: 'Foundation Models',
    type: 'code',
    showInColumn: 1,
    description: 'Foundation model information',
  },

  // Description field (full width)
  {
    name: 'businessMetadata.descriptiveMetadata.description',
    label: 'Description',
    description: 'Endpoint description',
  },
];

const customQueryForListPage = `
  MLEndpoint {
    route_optimized
    task
  }
`;

const customQueryForDetailsPage = `
  MLEndpoint {
    creator_kind
    permissions
    route_optimized
    task
    tags
    oauth_info
    custom_entities {
      entity_name
      llm_optimized
      max_dbus
      max_throughput
      min_dbus
      min_throughput
      name
      state
      traffic_percentage
      version
      workload_size
      workload_type
    }
    foundational_entities {
      display_name
      input_price
      model_class
      model_name
      name
      price
      price_unit
      pricing_model
      traffic_percentage
      type
    }
  }
`;

export const MLEndpointConfig: DataSource = {
  id: '15',
  name: 'MLEndpoint',
  category: 'servingEndpoint',
  label: 'Serving Endpoints',
  dataSourceLabel: 'ML Serving Endpoints',
  showColumns: false,
  showPartitionColumns: false,
  showLineage: false,
  showLineageTable: false,
  showLineageQuery: false,
  showLineageCloned: false,
  showLineageCW: false,
  showLineagePipeline: false,
  showLineageDashboard: false,
  gridHeaders,
  attributesMap,
  customQueryForListPage,
  customQueryForDetailsPage,
};
